import { useMemo } from 'react';
import fuzzysort from 'fuzzysort';
import useGetAddressAutocomplete from '../../../../../utils/apis/useAddressAutocomplete';

function useFuzzySearchedPreviousListings(previouslyEnteredListings, partialAddress) {
  const fuzzySortPreparedTargets = useMemo(() => {
    return previouslyEnteredListings.map((listing) => listing.address).map(fuzzysort.prepare);
  }, [previouslyEnteredListings]);
  const filteredPreviouslyEnteredListings = fuzzysort.go(partialAddress, fuzzySortPreparedTargets, {
    all: true,
  });
  return filteredPreviouslyEnteredListings;
}

/**
 * Returns the list of options to be used in the address search
 * If the entire string matches a previously entered listing, we will only show that entry.
 * @param {string[]} addressSuggestions
 * @param {FormattedListing[]} previouslyEnteredListings
 * @param {string} partialAddress
 */
export function useAddressOptions(addressSuggestions, previouslyEnteredListings, partialAddress) {
  const filteredPreviouslyEnteredListings = useFuzzySearchedPreviousListings(previouslyEnteredListings, partialAddress);
  // Case when selected value is a previously entered address
  if (previouslyEnteredListings.some((listing) => listing.address === partialAddress)) {
    return [['Previously Entered Addresses', partialAddress]];
  }
  if (filteredPreviouslyEnteredListings.length > 0 && addressSuggestions.length > 0) {
    return [
      ['Previously Entered Addresses', ...filteredPreviouslyEnteredListings.map((result) => result.target)],
      ['Suggestions', ...addressSuggestions],
    ];
  }
  if (filteredPreviouslyEnteredListings.length > 0) {
    return [['Previously Entered Addresses', ...filteredPreviouslyEnteredListings.map((result) => result.target)]];
  }
  if (addressSuggestions.length > 0) {
    return addressSuggestions;
  }
  return [];
}

export function validateAddress(address, addressSuggestions, previouslyEnteredListings) {
  const previouslyEnteredListingAddresses = new Set(previouslyEnteredListings.map((listing) => listing.address));
  if (!address) {
    return 'Please enter a value';
  }
  if (previouslyEnteredListingAddresses.has(address)) {
    return undefined;
  }
  if (address.length < 7) {
    return 'Please enter at least 7 characters';
  }
  if (addressSuggestions.length === 0) {
    return `Unable to find a USPS address matching "${address}"`;
  }
  if (!addressSuggestions.some((addressSuggestion) => addressSuggestion === address)) {
    return 'Please select a value from the dropdown';
  }
  return undefined;
}

/**
 * @returns {import('react-query').UseQueryResult}
 */
export function useAddressAutocomplete(partialAddress) {
  const { data = [], ...rest } = useGetAddressAutocomplete(partialAddress, {
    enabled: partialAddress?.length > 6,
    select(responseData) {
      return responseData.data.response.addresses.map((address) => address.name);
    },
  });
  return { ...rest, data };
}
